import React, { Fragment, useState } from "react";

// External Components
import Modal from "react-bootstrap/Modal";
import Checkbox from "@material-ui/core/Checkbox";
import Spinner from "react-bootstrap/Spinner";

// Icons
import downloadFileIcon from "../../../../assets/icons/download-file.svg";
import previewFile from "../../../../assets/icons/preview-file.svg";
import CloseIcon from "../../../../assets/icons/close.svg";

// Stylesheets
import "./DocumentStatus.scss";

// Custom Components
import DocumentPreview from "../DocumentPreview/DocumentPreview";

// Apollo Client
import { useQuery, gql, useMutation } from "@apollo/client";

// External Libraries
import { useImmer } from "use-immer";
import { notify } from "../../../Notify/Notify";
import { sort } from "fast-sort";

// Utilities
import { downloadFile } from "../../../../helper";

// Graphql Related
const GET_ALL_DOCS_OF_USER = gql`
  query getAllDocsOfTheUser($id: String!) {
    data: documentrequestUserDetails(id: $id) {
      id
      submissionStatus
      submissions {
        id
        documentFileInfo: documentRequestDocument {
          id
          documentName
        }
        isPrimary
        primaryDocument {
          id
        }
        status: submissionStatus
      }
    }
  }
`;

const MERGE_DOCUMENT_REQUEST_DOCUMENTS = gql`
  mutation MergeDocumentRequestDocuments(
    $primaryDocumentId: String!
    $nonPrimaryDocumentIds: [String!]!
  ) {
    mergeDocumentRequestDocuments(
      primaryDocumentId: $primaryDocumentId
      nonPrimaryDocumentIds: $nonPrimaryDocumentIds
    ) {
      success
    }
  }
`;

const UNMERGE_DOCUMENT_REQUEST_DOCUMENT = gql`
  mutation UnmergeDocumentRequestDocument($primaryDocumentId: String!) {
    unmergeDocumentRequestDocument(primaryDocumentId: $primaryDocumentId) {
      success
    }
  }
`;

export const GET_PRESIGNED_URL = gql`
  mutation GetPresignedUrlForSubmission($documentRequestSubmissionId: String!) {
    data: downloadDocumentRequestSubmission(
      documentRequestSubmissionId: $documentRequestSubmissionId
    ) {
      success
      url
    }
  }
`;

function DocumentStatus({
  isOpen = false,
  onClose = () => {},
  userId = "",
  sendReminderHandler = () => {},
  isSendReminderDisabled = false,
}) {
  // State hooks
  // State hooks
  const [isFileForPreviewOpen, setIsFileForPreviewOpen] = React.useState(false);
  const [fileForPreview, setFileForPreview] = useImmer({
    documentId: "",
    title: "",
    url: "",
    isFromMarge: true,
  });

  // Query hooks
  const { loading: isLoadingDocs, data: { data: userData = {} } = {} } =
    useQuery(GET_ALL_DOCS_OF_USER, {
      fetchPolicy: "cache-and-network",
      variables: { id: userId },
      skip: !userId,
    });

  const { submissions: allSubmissions = [], submissionStatus = "PENDING" } =
    userData;

  // Filter submissions based on status
  const pendingDocuments = allSubmissions
    .filter((submission) => submission.status?.toLowerCase() === "pending")
    .map((submission) => submission.documentFileInfo?.documentName);

  const submittedDocuments = allSubmissions
    .filter(
      (submission) =>
        submission.status?.toLowerCase() === "submitted" &&
        !submission.isPrimary &&
        !submission.primaryDocument
    )
    .map((submission) => ({
      documentId: submission.documentFileInfo?.id,
      documentName: submission.documentFileInfo?.documentName,
      id: submission.id,
    }));

  const mergedDocuments = allSubmissions
    .filter(
      (submission) =>
        submission.status?.toLowerCase() === "submitted" &&
        (submission.isPrimary || submission.primaryDocument !== null)
    )
    .map((submission) => ({
      documentId: submission.documentFileInfo?.id,
      documentName: submission.documentFileInfo?.documentName,
      id: submission.id,
      isPrimary: submission.isPrimary,
      primaryDocument: submission.primaryDocument,
    }));

  // Handlers
  const previewFileHandler = (
    fileInfo = {},
    shouldChangeThePopupState = true,
    isFromMarge = true
  ) => {
    setFileForPreview((draft) => {
      draft.documentId = fileInfo.documentId ?? "no-document-id-coming";
      draft.title = fileInfo.title ?? "no-document-title-coming";
      draft.url = fileInfo.url;
      draft.isFromMarge = isFromMarge;
    });
    if (shouldChangeThePopupState) {
      setIsFileForPreviewOpen(true);
    }
  };

  const onCloseHandlerForFilePreview = () => {
    setFileForPreview({
      documentId: "",
      title: "",
      url: "",
    });
    setIsFileForPreviewOpen(false);
  };

  const [unmergeDocuments, { loading: isUnmergeDocumentsLoading }] =
    useMutation(UNMERGE_DOCUMENT_REQUEST_DOCUMENT, {
      refetchQueries: [
        { query: GET_ALL_DOCS_OF_USER, variables: { id: userId } },
      ],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Unmerge Document Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Documents are successfully unmerged.", "success");
      },
      awaitRefetchQueries: true,
    });

  const [setDocsToPrimary, { loading: isSetDocsToPrimaryLoading }] =
    useMutation(MERGE_DOCUMENT_REQUEST_DOCUMENTS, {
      refetchQueries: [
        { query: GET_ALL_DOCS_OF_USER, variables: { id: userId } },
      ],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Merge Document Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Primary document has been modified.", "success");
      },
      awaitRefetchQueries: true,
    });

  const setDocsToPrimaryHandler = (primaryDocumentId) => {
    const nonPrimaryDocumentIds = mergedDocuments
      .filter((doc) => doc.id !== primaryDocumentId)
      .map((doc) => doc.id);
    setDocsToPrimary({
      variables: {
        primaryDocumentId,
        nonPrimaryDocumentIds,
      },
    });
  };
  return (
    <Fragment>
      <Modal
        show={isOpen}
        dialogClassName="new-design-v1 document-status-container"
        centered
        onHide={onClose}
      >
        <Modal.Header>
          <h4 className="modal-title">{"Document List"}</h4>
          <img
            className="close"
            src={CloseIcon}
            alt="closeIcon"
            onClick={onClose}
          />
        </Modal.Header>
        <Modal.Body className="document-status-body p-5">
          {!isLoadingDocs && (
            <Fragment>
              {mergedDocuments?.length > 0 && (
                <div>
                  <section className="document-status-body-submitted">
                    <div className="document-status-body-submitted-label mb-4">
                      {"Merged"}
                    </div>
                  </section>
                  <div className="d-flex flex-column">
                    {sort(mergedDocuments)
                      .by([
                        { desc: (u) => u.isPrimary },
                        { asc: (u) => u.documentName?.toLowerCase() },
                      ])

                      ?.map((document, index) => (
                        <MargeDocument
                          title={document.documentName}
                          documentId={document.id}
                          previewFileHandler={previewFileHandler}
                          index={index}
                          isPrimary={Boolean(document?.isPrimary)}
                          setADocsToPrimaryHandler={setDocsToPrimaryHandler}
                          isSetADocsToPrimaryLoading={isSetDocsToPrimaryLoading}
                        />
                      ))}
                    <button
                      className="unlink-merging-btn"
                      onClick={() => {
                        unmergeDocuments({
                          variables: {
                            primaryDocumentId:
                              mergedDocuments?.find((document) =>
                                Boolean(document?.isPrimary)
                              )?.id ?? "no-id-coming",
                          },
                        });
                      }}
                      disabled={isUnmergeDocumentsLoading}
                    >
                      {"Unlink Merging"}
                    </button>
                  </div>
                </div>
              )}
              {!(
                mergedDocuments?.length !== 0 &&
                submittedDocuments?.length === 0
              ) && (
                <DocumentSection
                  title="Submitted"
                  documents={submittedDocuments}
                  previewFileHandler={previewFileHandler}
                  userId={userId}
                  mergedDocuments={mergedDocuments ?? []}
                />
              )}

              <div>
                <section className="document-status-body-submitted">
                  <div className="document-status-body-submitted-label mb-4">
                    {"Pending"}
                  </div>
                </section>
                <div className="d-flex flex-column" style={{ gap: "16px" }}>
                  {pendingDocuments.map((document, index) => (
                    <PendingDocument key={index} documentName={document} />
                  ))}
                </div>
              </div>
            </Fragment>
          )}
          {isLoadingDocs && (
            <div className="h-100 d-flex justify-content-center align-items-center">
              <Spinner animation="border" role="status" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="secondary-button mr-4"
            style={{ maxWidth: "79px" }}
            onClick={onClose}
          >
            {"Cancel"}
          </button>
          <button
            className="primary-button"
            style={{ maxWidth: "133px" }}
            disabled={submissionStatus !== "PENDING" || isSendReminderDisabled}
            onClick={sendReminderHandler}
          >
            {"Send Reminder"}
          </button>
        </Modal.Footer>
      </Modal>
      <DocumentPreview
        isOpen={isFileForPreviewOpen}
        onClose={onCloseHandlerForFilePreview}
        fileInfo={fileForPreview}
        mergedDocuments={mergedDocuments}
        previewFileHandler={previewFileHandler}
      />
    </Fragment>
  );
}

export default DocumentStatus;

// Helper components

// Marge docs helper

function MargeDocument({
  index = 1,
  documentId = "",
  title = "",
  isPrimary = false,
  previewFileHandler = () =>
    console.log("previewFileHandler is not coming to MargeDocument"),
  isSetADocsToPrimaryLoading = false,
  setADocsToPrimaryHandler = () => {},
}) {
  const [getPresignedUrl, { loading }] = useMutation(GET_PRESIGNED_URL);
  return (
    <div
      className="document-status-body-submitted-merged-card"
      style={{
        borderTopLeftRadius: index === 0 ? "8px" : "0px",
        borderTopRightRadius: index === 0 ? "8px" : "0px",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mr-5">
        <div className="font-size-14 text-color-020617">{title}</div>
        {isPrimary && (
          <div
            className="docs-action-btn docs-action-btn-primary px-3 py-1"
            style={{ cursor: "context-menu" }}
          >
            {"Primary"}
          </div>
        )}
        {!isPrimary && (
          <button
            className="docs-action-btn docs-action-btn-non-primary px-3 py-1"
            onClick={() => setADocsToPrimaryHandler(documentId)}
            disabled={isSetADocsToPrimaryLoading}
          >
            {"Set as Primary"}
          </button>
        )}
      </div>
      <div className="document-status-body-submitted-card-item-action">
        <div></div>
        <img
          src={downloadFileIcon}
          alt="download-file"
          className="cursor-pointer action-img"
          style={{ opacity: loading ? 0.5 : 1 }}
          onClick={async () => {
            if (loading) {
              return;
            }
            try {
              const data = await getPresignedUrl({
                variables: { documentRequestSubmissionId: documentId },
              });
              const url = data?.data?.data?.url;
              if (url) {
                downloadFile(url, `${title}.pdf`);
                notify("Success", "Download will start soon.", "success");
              }
            } catch (error) {
              notify("Error", "Something went wrong.", "error");
            }
          }}
        />
        <img
          src={previewFile}
          alt="preview-file"
          className="cursor-pointer action-img"
          style={{ opacity: loading ? 0.5 : 1 }}
          onClick={async () => {
            try {
              if (loading) {
                return;
              }
              const data = await getPresignedUrl({
                variables: { documentRequestSubmissionId: documentId },
              });
              const url = data?.data?.data?.url;
              if (url) {
                previewFileHandler({
                  documentId,
                  title,
                  url,
                });
              }
            } catch (error) {
              notify("Error", "Something went wrong.", "error");
            }
          }}
        />
      </div>
    </div>
  );
}
// Submitted  docs Helper
function DocumentCard({
  documentId = "",
  title = "",
  isChecked = false,
  selectedDocsHandler = () => {},
  previewFileHandler = () =>
    console.log("previewFileHandler is not coming to DocumentCard"),
}) {
  const [getPresignedUrl, { loading }] = useMutation(GET_PRESIGNED_URL);
  return (
    <div className="document-status-body-submitted-card">
      <Checkbox
        color="primary"
        onChange={() => {
          selectedDocsHandler(documentId);
        }}
        checked={isChecked}
      />
      <div className="document-status-body-submitted-card-item">
        <div className="pl-5">{title}</div>
        <div className="document-status-body-submitted-card-item-action">
          <div></div>
          <img
            src={downloadFileIcon}
            alt="download-file"
            className="cursor-pointer action-img"
            style={{ opacity: loading ? 0.5 : 1 }}
            onClick={async () => {
              if (loading) {
                return;
              }
              try {
                const data = await getPresignedUrl({
                  variables: { documentRequestSubmissionId: documentId },
                });
                const url = data?.data?.data?.url;
                if (url) {
                  downloadFile(url, `${title}.pdf`);
                  notify("Success", "Download will start soon.", "success");
                }
              } catch (error) {
                notify("Error", "Something went wrong.", "error");
              }
            }}
          />
          <img
            src={previewFile}
            alt="preview-file"
            className="cursor-pointer action-img"
            style={{ opacity: loading ? 0.5 : 1 }}
            onClick={async () => {
              if (loading) {
                return;
              }
              try {
                const data = await getPresignedUrl({
                  variables: { documentRequestSubmissionId: documentId },
                });
                const url = data?.data?.data?.url;
                if (url) {
                  previewFileHandler(
                    {
                      documentId,
                      title,
                      url,
                    },
                    true,
                    false
                  );
                }
              } catch (error) {
                notify("Error", "Something went wrong.", "error");
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

function DocumentSection({
  userId = "",
  title,
  documents = [],
  previewFileHandler = () =>
    console.log("previewFileHandler is not coming to DocumentSection"),
  mergedDocuments = [],
}) {
  const [selectedDocs, setSelectedDocs] = useState(() => new Set());
  const selectedDocsHandler = (id) => {
    // Create a copy of the selected checkboxes set
    const updatedSelectedDocs = new Set(selectedDocs);
    // If checkbox is already selected, remove it, otherwise add it
    if (updatedSelectedDocs.has(id)) {
      updatedSelectedDocs.delete(id);
    } else {
      updatedSelectedDocs.add(id);
    }
    // Update state with the new set of selected checkboxes
    setSelectedDocs(updatedSelectedDocs);
  };

  const [mergeDocuments, { loading: isMargeDocumentsMutationLoading }] =
    useMutation(MERGE_DOCUMENT_REQUEST_DOCUMENTS, {
      refetchQueries: [
        { query: GET_ALL_DOCS_OF_USER, variables: { id: userId } },
      ],
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Marge Document Mutation Error:", error);
      },
      onCompleted: () => {
        setSelectedDocs(new Set());
        const title = "Success";
        const detail = `Documents are successfully merged.`;
        const type = "success";
        notify(title, detail, type);
      },
      awaitRefetchQueries: true,
    });
  const mergeDocsHandler = () => {
    let variables = {};
    if (mergedDocuments.length > 0) {
      const primaryDocumentId = mergedDocuments.filter(
        (doc) => doc.isPrimary
      )[0]?.id;
      const nonPrimaryDocumentIds = [
        ...mergedDocuments
          .filter((doc) => doc.isPrimary === false)
          .map((doc) => doc.id),
        ...Array.from(selectedDocs),
      ];
      variables = {
        primaryDocumentId,
        nonPrimaryDocumentIds,
      };
    } else {
      const [primaryDocumentId, ...nonPrimaryDocumentIds] =
        Array.from(selectedDocs);
      variables = {
        primaryDocumentId,
        nonPrimaryDocumentIds,
      };
    }
    mergeDocuments({
      variables,
    });
  };
  return (
    <div className="d-flex flex-column" style={{ gap: "16px" }}>
      <section className="document-status-body-submitted">
        <div className="document-status-body-submitted-label mb-4">{title}</div>
      </section>
      {documents.map((document, index) => (
        <Fragment key={document.id}>
          <DocumentCard
            key={index}
            title={document.documentName}
            documentId={document.id}
            previewFileHandler={previewFileHandler}
            isChecked={selectedDocs.has(document.id)}
            selectedDocsHandler={selectedDocsHandler}
          />
        </Fragment>
      ))}
      {documents?.length > 0 && (
        <button
          className="light-primary-button mt-4"
          disabled={selectedDocs.size === 0 || isMargeDocumentsMutationLoading}
          onClick={mergeDocsHandler}
        >
          Merge Selected
        </button>
      )}
      {documents?.length === 0 && (
        <div className="w-100 font-size-14 text-color-020617 text-center">
          {" "}
          {"Currently, no documents have been submitted."}
        </div>
      )}
    </div>
  );
}

// Pending docs helper
function PendingDocument({ documentName }) {
  return (
    <section className="document-status-body-submitted-pending-card">
      {documentName}
    </section>
  );
}
